import Papa from 'papaparse';
import type { Guest, ValidationError, CsvPreviewData, HeaderMapping, CsvImportResult, FieldConfig, CsvImportVersionChoice, CustomFieldConfig } from './types.ts';
import { $guests, $customFields, versionActions, $currentVersionId, $availableVersions } from './state.ts';
import { historyAwareGuestActions, historyNavigation } from './history.ts';
import { storageManager } from './storage.ts';
import { showCsvVersionChoiceDialog } from './csv-version-dialog.ts';
import { showNotification } from './notifications.ts';
import { DialogHelper } from './dialog.ts';
import { exportToPDF } from './drawing.ts';
import { validateSeatPair, validateSeatRange } from './domain/validation';

// --- CSV Generation ---

/**
 * Generates CSV content from a list of guests.
 */
export function generateCsvContent(guests: Guest[]): string {
  const customFieldConfigs = $customFields.get();
  const headers = ['Full Name', 'Table', 'Seat', ...customFieldConfigs.map(c => c.displayName)];
  const rows = [headers];

  guests.forEach(guest => {
    const row = [
      escapeCsvValue(guest.fullName),
      escapeCsvValue(guest.table?.toString() ?? ''),
      escapeCsvValue(guest.seat?.toString() ?? '')
    ];
    customFieldConfigs.forEach(config => {
      const value = (guest as any)[config.key] ?? '';
      row.push(escapeCsvValue(value));
    });
    rows.push(row);
  });
  
  return rows.map(row => row.join(',')).join('\n');
}

/**
 * Escapes a value for CSV format.
 */
function escapeCsvValue(value: string | number): string {
  const strValue = String(value);
  const needsQuote = strValue.includes(',') || strValue.includes('"') || strValue.includes('\n');
  let escaped = strValue.replace(/"/g, '""');
  if (needsQuote) {
    escaped = `"${escaped}"`;
  }
  return escaped;
}

/**
 * Triggers a download of the current guest list as a CSV file.
 */
export function exportToCsv(): void {
  const guests = $guests.get();
  const csvContent = generateCsvContent(guests);
  
  const currentVersionId = $currentVersionId.get();
  const currentVersion = $availableVersions.get().find(v => v.id === currentVersionId);
  const versionName = currentVersion ? currentVersion.name.replace(/ /g, '_') : 'Untitled';
  
  const now = new Date();
  const dateString = now.toISOString().split('T')[0];
  const filename = `${versionName}-${dateString}.csv`;
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  
  const placedCount = guests.filter(g => g.table && g.seat).length;
  const unplacedCount = guests.filter(g => !g.table || !g.seat).length;
  showNotification('Export Successful', `Downloaded ${guests.length} guests to ${filename} (${placedCount} placed, ${unplacedCount} unplaced)`);
}

// --- CSV Parsing and Data Conversion ---

const FIELD_CONFIGS: FieldConfig[] = [
  { key: 'name', label: 'Guest Name', required: true, matchers: ['full name', 'name', 'guest name', 'guest', 'fullname', 'participant'], fallbackIndex: 0 },
  { key: 'table', label: 'Table Number', required: false, matchers: ['table', 'table number', 'table #', 'tablenumber', 'table_number', 'dinner table', 'tables'], fallbackIndex: 1 },
  { key: 'seat', label: 'Seat Number', required: false, matchers: ['seat', 'seat number', 'seat #', 'seatnumber', 'seat_number', 'dinner seat'], fallbackIndex: 2 }
];

/**
 * Detects header mapping from CSV headers to guest fields.
 */
export function detectHeaderMapping(headers: string[]): HeaderMapping {
  const mapping: HeaderMapping = { name: null, table: null, seat: null, customField1: null, customField2: null };
  const lowerCaseHeaders = headers.map(h => h.toLowerCase().trim());
  
  FIELD_CONFIGS.forEach(config => {
    // Try to find a match based on keywords
    let matchIndex = lowerCaseHeaders.findIndex(h => config.matchers.includes(h));
    if (matchIndex !== -1) {
      mapping[config.key] = headers[matchIndex];
    }
  });

  // Fallback to positional mapping if no keyword match was found
  FIELD_CONFIGS.forEach(config => {
    if (!mapping[config.key] && config.fallbackIndex !== undefined && headers.length > config.fallbackIndex) {
      // Ensure the fallback index isn't already mapped
      const isIndexMapped = Object.values(mapping).includes(headers[config.fallbackIndex]);
      if (!isIndexMapped) {
        mapping[config.key] = headers[config.fallbackIndex];
      }
    }
  });
  
  // Suggest unmapped columns as custom fields
  const mappedHeaders = new Set(Object.values(mapping).filter(Boolean));
  const unmappedHeaders = headers.filter(h => !mappedHeaders.has(h));
  if (unmappedHeaders.length > 0) mapping.customField1 = unmappedHeaders[0];
  if (unmappedHeaders.length > 1) mapping.customField2 = unmappedHeaders[1];
  
  return mapping;
}

/**
 * Converts parsed CSV rows into an array of Guest objects.
 * Exported for testing purposes.
 */
export function rowsToGuests(rows: Record<string, string>[], headerMapping: HeaderMapping): CsvImportResult {
  const guests: Guest[] = [];
  const nameCounter = new Map<string, number>();
  let skippedRows = 0;
  
  rows.forEach((row, index) => {
    const nameValue = headerMapping.name ? (row[headerMapping.name] || '').trim() : '';
    
    if (!nameValue) {
      skippedRows++;
      return;
    }
    
    let fullName = nameValue;
    nameCounter.set(fullName, (nameCounter.get(fullName) || 0) + 1);
    if (nameCounter.get(fullName)! > 1) {
      fullName = `${fullName} (${nameCounter.get(fullName)})`;
    }
    
    const tableValue = headerMapping.table ? (row[headerMapping.table] || '').trim() : '';
    const seatValue = headerMapping.seat ? (row[headerMapping.seat] || '').trim() : '';
    
    const parsedTable = tableValue ? parseInt(tableValue, 10) : NaN;
    const parsedSeat = seatValue ? parseInt(seatValue, 10) : NaN;
    
    const guest: Guest = {
      id: Date.now() + index + Math.random(),
      fullName,
      table: !isNaN(parsedTable) ? parsedTable : null,
      seat: !isNaN(parsedSeat) ? parsedSeat : null,
      customField1: headerMapping.customField1 ? (row[headerMapping.customField1] || '').trim() : undefined,
      customField2: headerMapping.customField2 ? (row[headerMapping.customField2] || '').trim() : undefined
    };

    // Clean up empty custom fields
    if (!guest.customField1) delete guest.customField1;
    if (!guest.customField2) delete guest.customField2;
    
    guests.push(guest);
  });
  
  return { data: guests, errors: validateGuestData(guests), skippedRows };
}

/**
 * Validates guest data for conflicts and errors.
 */

export function validateGuestData(guests: Guest[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const seenSeats = new Set<string>();

  guests.forEach((guest, index) => {
    const row = index + 2;

    if (!guest.fullName) {
      errors.push({ row, message: 'Missing guest name' });
    }

    // pair consistency
    const pairErr = validateSeatPair(guest.table, guest.seat);
    if (pairErr) {
      errors.push({ row, message: pairErr });
    }

    if (guest.table !== null && guest.seat !== null) {
      const seatKey = `${guest.table}-${guest.seat}`;
      // seat conflict
      if (seenSeats.has(seatKey)) {
        errors.push({ row, message: `Seat conflict at Table ${guest.table}, Seat ${guest.seat}` });
      }
      seenSeats.add(seatKey);

      // range checks
      const rangeErr = validateSeatRange(guest.table, guest.seat);
      if (rangeErr) {
        errors.push({ row, message: rangeErr });
      }
    }
  });

  return errors;
}
// --- CSV Import UI and Workflow ---

/**
 * Main entry point for handling a file import from the user.
 */
export function handleFileImport(event: Event): void {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.trim(),
    complete: (result) => {
      target.value = ''; // Reset file input
      if (!result.data || result.data.length === 0) {
        showNotification('Import Error', 'No data rows found in CSV file.', 'error');
        return;
      }
      
      const headers = result.meta.fields || [];
      const parsedData = result.data as Record<string, string>[];
      const previewData: CsvPreviewData = {
        headers,
        rows: parsedData.map(row => headers.map(h => row[h] || '')),
        totalRows: parsedData.length,
        parsedData
      };
      
      showImportPreviewDialog(previewData, detectHeaderMapping(headers));
    },
    error: (err) => {
      target.value = ''; // Reset file input
      showNotification('Import Error', `Failed to parse CSV: ${err.message}`, 'error');
    }
  });
}

/**
 * Displays the CSV import preview and mapping dialog.
 */
async function showImportPreviewDialog(previewData: CsvPreviewData, defaultMapping: HeaderMapping) {
  const dialog = new DialogHelper();
  let currentMapping = { ...defaultMapping };
  
  const template = document.getElementById('csv-preview-template') as HTMLTemplateElement;
  const templateContent = template.content.cloneNode(true) as DocumentFragment;
  
  // Create a proper container element to avoid DocumentFragment querySelector issues
  const contentElement = document.createElement('div');
  contentElement.appendChild(templateContent);

  // --- UI Population and Logic ---
  populatePreview(contentElement, previewData);
  populateMapping(contentElement, previewData, currentMapping);
  updateValidationDisplay(contentElement, previewData, currentMapping);

  // --- Event Listeners ---
  contentElement.querySelectorAll('.header-mapping-select').forEach(select => {
    select.addEventListener('change', () => {
      currentMapping = getCurrentMapping(contentElement);
      updateValidationDisplay(contentElement, previewData, currentMapping);
    });
  });

  // --- Show Dialog ---
  dialog.show(
    { title: 'CSV Import Preview', content: contentElement, className: 'csv-import-modal' },
    [
      { text: 'Cancel', handler: () => dialog.close() },
      { text: 'Import Data', primary: true, handler: async () => {
          if (!validateMappingRequirements(currentMapping)) return;
          
          const result = rowsToGuests(previewData.parsedData, currentMapping);
          dialog.close(); // Close preview dialog before showing next one
          
          const versionChoice = await showCsvVersionChoiceDialog(result.data.length);
          await executeImport(result, versionChoice, currentMapping);
        }
      }
    ]
  );
}

function populatePreview(element: HTMLElement, data: CsvPreviewData) {
  (element.querySelector('.preview-summary') as HTMLElement).textContent = `Found ${data.totalRows} rows of data.`;
  
  const thead = element.querySelector('.csv-preview-table thead') as HTMLTableSectionElement;
  const tbody = element.querySelector('.csv-preview-table tbody') as HTMLTableSectionElement;
  
  const headerRow = thead.insertRow();
  data.headers.forEach(header => {
    const th = document.createElement('th');
    th.textContent = header;
    headerRow.appendChild(th);
  });

  data.rows.forEach(row => {
    const tr = tbody.insertRow();
    row.forEach(cell => tr.insertCell().textContent = cell || '');
  });
}

function populateMapping(element: HTMLElement, data: CsvPreviewData, mapping: HeaderMapping) {
  const container = element.querySelector('.mapping-fields') as HTMLElement;
  const rowTemplate = document.getElementById('header-mapping-row-template') as HTMLTemplateElement;

  const allFields = [
    ...FIELD_CONFIGS,
    { separator: true },
    { key: 'customField1', label: 'Custom Field 1', required: false },
    { key: 'customField2', label: 'Custom Field 2', required: false }
  ];

  allFields.forEach(config => {
    if ((config as any).separator) {
      const separator = document.createElement('div');
      separator.className = 'mapping-separator';
      separator.textContent = 'Custom Fields (optional)';
      container.appendChild(separator);
      return;
    }
    
    const row = rowTemplate.content.cloneNode(true) as DocumentFragment;
    const label = row.querySelector('.header-mapping-label') as HTMLLabelElement;
    const select = row.querySelector('.header-mapping-select') as HTMLSelectElement;

    label.textContent = `${config.label}${config.required ? ' *' : ''}:`;
    select.dataset.field = config.key;

    // Add header options
    data.headers.forEach(header => {
      const option = new Option(header, header);
      select.add(option);
    });
    
    // Set the selected value based on mapping
    const mappedHeader = mapping[config.key as keyof HeaderMapping];
    if (mappedHeader) {
      select.value = mappedHeader;
    }
    // If no mapping or mapping is null, the default "-- Skip this field --" option remains selected

    container.appendChild(row);
  });
}

function getCurrentMapping(element: HTMLElement): HeaderMapping {
  const mapping: any = {};
  element.querySelectorAll<HTMLSelectElement>('.header-mapping-select').forEach(select => {
    if (select.dataset.field) {
      mapping[select.dataset.field] = select.value || null;
    }
  });
  return mapping as HeaderMapping;
}

function updateValidationDisplay(element: HTMLElement, data: CsvPreviewData, mapping: HeaderMapping) {
  const result = rowsToGuests(data.parsedData, mapping);
  const errorsContainer = element.querySelector('.validation-errors') as HTMLElement;
  const errorsList = errorsContainer.querySelector('.error-list') as HTMLUListElement;
  errorsList.innerHTML = '';

  if (result.errors.length === 0) {
    errorsContainer.style.display = 'none';
  } else {
    errorsContainer.style.display = 'block';
    result.errors.forEach(error => {
      const li = document.createElement('li');
      li.textContent = `Row ${error.row}: ${error.message}`;
      errorsList.appendChild(li);
    });
  }
}

export function validateMappingRequirements(mapping: HeaderMapping): boolean {
  const missing = FIELD_CONFIGS.filter(c => c.required && !mapping[c.key]).map(c => c.label);
  if (missing.length > 0) {
    showNotification('Import Error', `Required field missing: ${missing.join(', ')}`, 'error');
    return false;
  }
  return true;
}

/**
 * Executes the final import logic after user choices are made.
 */
async function executeImport(result: CsvImportResult, versionChoice: CsvImportVersionChoice, mapping: HeaderMapping) {
  // Update custom field configuration based on mapping
  const newCustomFields: CustomFieldConfig[] = [];
  if (mapping.customField1) newCustomFields.push({ key: 'customField1', displayName: mapping.customField1, visibleInChart: true, visibleInList: true });
  if (mapping.customField2) newCustomFields.push({ key: 'customField2', displayName: mapping.customField2, visibleInChart: true, visibleInList: true });
  
  if (versionChoice.choice === 'new_version' && versionChoice.newVersionName) {
    const newVersion = await storageManager.createVersion(versionChoice.newVersionName, {
      guests: result.data,
      customFields: newCustomFields
    });
    versionActions.addVersion(newVersion);
    versionActions.setCurrentVersion(newVersion.id);
    $guests.set(newVersion.state.guests);
    $customFields.set(newVersion.state.customFields);
    historyNavigation.loadVersionHistory(newVersion.history || []);
    await storageManager.setLastUsedVersion(newVersion.id);
    showNotification('Import Successful', `Created version "${versionChoice.newVersionName}" with ${result.data.length} guests.`);
  } else {
    // Overwrite current version
    $customFields.set(newCustomFields);
    historyAwareGuestActions.importGuests(
      result.data.map(guest => ({
        fullName: guest.fullName,
        table: guest.table || undefined,
        seat: guest.seat || undefined,
        customField1: guest.customField1,
        customField2: guest.customField2
      })),
      `CSV Import (${result.data.length} guests)`
    );
    showNotification('Import Successful', `Imported ${result.data.length} guests into the current version.`);
  }
  
  if (result.errors.length > 0) {
    showNotification('Import Warning', `${result.errors.length} validation issues found.`, 'warning');
  }
}

// --- DOM Initialization ---
document.addEventListener('DOMContentLoaded', () => {
  document.getElementById('exportBtn')?.addEventListener('click', exportToCsv);
  document.getElementById('exportPdfBtn')?.addEventListener('click', exportToPDF);
  document.getElementById('csvFileInput')?.addEventListener('change', handleFileImport);
}); 